import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseKey);

// Translate query params like _limit or key=value to Supabase queries
const applyQueryString = (query: any, urlStr: string) => {
    try {
        const url = new URL(urlStr, 'http://localhost');
        const params = url.searchParams;

        // Custom operators
        params.forEach((value, key) => {
            if (key === '_limit') {
                query = query.limit(parseInt(value));
            } else if (key === '_order') {
                const parts = value.split('.');
                const column = parts[0];
                const ascending = parts.length > 1 ? parts[1].toLowerCase() === 'asc' : true;
                query = query.order(column, { ascending });
            } else if (value.startsWith('neq.')) {
                let val: any = value.replace('neq.', '');
                if (val === 'true') val = true;
                if (val === 'false') val = false;
                if (val === 'null') val = null;
                query = query.neq(key, val);
            } else if (value.startsWith('eq.')) {
                let val: any = value.replace('eq.', '');
                if (val === 'true') val = true;
                if (val === 'false') val = false;
                if (val === 'null') val = null;
                query = query.eq(key, val);
            } else {
                let val: any = value;
                if (val === 'true') val = true;
                if (val === 'false') val = false;
                if (val === 'null') val = null;
                query = query.eq(key, val);
            }
        });
        return query;
    } catch (e) {
        return query;
    }
};

export const api = {
    get: async (endpoint: string) => {
        // e.g. endpoint = "/data/profiles?email=test@test.com"
        const isDataRoute = endpoint.startsWith('/data/');
        if (!isDataRoute) {
             if (endpoint === '/public/reviews') {
                 const { data, error } = await supabase.from('reviews').select('*').order('created_at', { ascending: false });
                 if (error) throw error;
                 return data;
             }
             if (endpoint === '/ai/sessions') {
                 try {
                     const { data: { user } } = await supabase.auth.getUser();
                     if (!user) return [];
                     
                     // The get_user_chat_sessions RPC doesn't exist, so we aggregate manually
                     const { data, error } = await supabase
                         .from('ai_chat_logs')
                         .select('session_id, content, created_at, role')
                         .eq('user_id', user.id)
                         .order('created_at', { ascending: true }); // getting oldest first to capture 'first_message'
                         
                     if (error) throw error;
                     
                     const sessionsMap = new Map();
                     data?.forEach((log: any) => {
                         if (!sessionsMap.has(log.session_id)) {
                             sessionsMap.set(log.session_id, {
                                 session_id: log.session_id,
                                 first_message: log.role === 'user' ? log.content : "New Conversation",
                                 created_at: log.created_at
                             });
                         }
                     });
                     
                     // Convert map back to array and sort by newest first
                     return Array.from(sessionsMap.values()).sort((a: any, b: any) => 
                         new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                     );
                 } catch (e) {
                     console.warn("Could not load sessions", e);
                     return [];
                 }
             }
             throw new Error('Unsupported GET endpoint: ' + endpoint);
        }

        const urlParts = endpoint.replace('/data/', '').split('?');
        const table = urlParts[0];

        let query = supabase.from(table).select('*');
        if (urlParts.length > 1) {
            query = applyQueryString(query, endpoint.replace('/data/', ''));
        }

        const { data, error } = await query;
        if (error) throw error;
        return data;
    },

    post: async (endpoint: string, body: any) => {
        // Route auth admin actions to Edge Function
        if (endpoint.startsWith('/admin/users')) {
             const { data, error } = await supabase.functions.invoke('admin-user-manager', {
                body: { action: 'CREATE_USER', payload: body }
            });
            if (error) throw error;
            if (data?.error) throw new Error(data.error);
            return data;
        }

        if (endpoint === '/submit-contact') {
            const { data, error } = await supabase.functions.invoke('submit-contact', {
                body
            });
            if (error) throw error;
            if (data?.error) throw new Error(data.error);
            return data;
        }

        if (endpoint === '/ai-chat') {
            const { data, error } = await supabase.functions.invoke('ai-chat', {
                body
            });
            if (error) throw error;
            if (data?.error) throw new Error(data.error);
            return data;
        }

        const isDataRoute = endpoint.startsWith('/data/');
        if (!isDataRoute) throw new Error('Unsupported POST endpoint: ' + endpoint);

        const table = endpoint.replace('/data/', '');

        const { data, error } = await supabase.from(table).insert([body]).select();
        
        if (error) throw error;
        // The old API expects successful insert to maybe return an insertId or just the data.
        // Usually, the app wants the inserted object(s).
        return Array.isArray(data) && data.length > 0 ? data[0] : (data || { success: true });
    },

    put: async (endpoint: string, body: any) => {
        // Route auth admin actions to Edge Function
        if (endpoint.startsWith('/admin/users')) {
             const adminId = endpoint.split('/')[3] || endpoint.split('/')[2];
            const { data, error } = await supabase.functions.invoke('admin-user-manager', {
                body: { action: 'UPDATE_USER', payload: { userId: adminId, updates: body } }
            });
            if (error) throw error;
            if (data?.error) throw new Error(data.error);
            return data;
        }

        const isDataRoute = endpoint.startsWith('/data/');
        if (!isDataRoute) throw new Error('Unsupported PUT endpoint: ' + endpoint);

        const parts = endpoint.replace('/data/', '').split('/');
        const table = parts[0];
        const id = parts[1]; // /data/orders/:id

        if (!id) throw new Error('PUT requires an ID');

        const { data, error } = await supabase.from(table).update(body).eq('id', id).select();
        
        if (error) throw error;
        return Array.isArray(data) && data.length > 0 ? data[0] : (data || { success: true });
    },

    delete: async (endpoint: string) => {
        // Route auth admin actions to Edge Function
        if (endpoint.startsWith('/admin/users')) {
            const pathParts = endpoint.split('/');
            const adminId = pathParts[3] || pathParts[2]; // /admin/users/:id
            if (!adminId) throw new Error('Delete user requires an ID');
            const { data, error } = await supabase.functions.invoke('admin-user-manager', {
                body: { action: 'DELETE_USER', payload: { userId: adminId } }
            });
            if (error) throw error;
            if (data?.error) throw new Error(data.error);
            return { success: true };
        }

        const isDataRoute = endpoint.startsWith('/data/');
        if (!isDataRoute) throw new Error('Unsupported DELETE endpoint: ' + endpoint);

        // Can be /data/contact_submissions/:id OR /data/logs?level=INFO
        const urlParts = endpoint.replace('/data/', '').split('?');
        const pathParts = urlParts[0].split('/');
        const table = pathParts[0];
        const id = pathParts[1];

        let query = supabase.from(table).delete();

        if (id) {
            query = query.eq('id', id);
        } else if (urlParts.length > 1) {
            query = applyQueryString(query, endpoint.replace('/data/', ''));
        } else {
             throw new Error('Bulk delete without filters is unsafe');
        }

        const { error } = await query;
        if (error) throw error;
        return { success: true };
    },

    sendEmail: async (to: string, subject: string, html: string, type?: string, attachments?: any[]) => {
        const { data, error } = await supabase.functions.invoke('send-email', {
            body: { to, subject, html, type, attachments }
        });
        if (error) throw error;
        if (data?.error) throw new Error(JSON.stringify(data.error));
        return data;
    },

    auth: {
        signInWithPassword: async ({ email, password }: any) => {
            return await supabase.auth.signInWithPassword({ email, password });
        },

        signUp: async ({ email, password, options }: any) => {
            return await supabase.auth.signUp({ email, password, options });
        },

        signInWithIdToken: async ({ provider, token }: any) => {
            return await supabase.auth.signInWithIdToken({ provider, token });
        },

        getUser: async () => {
            const { data: authData, error: authError } = await supabase.auth.getUser();
            if (authError || !authData.user) return { data: { user: null }, error: authError };

            // Also fetch profile
            const { data: profile } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', authData.user.id)
                .single();

            const combinedUser = { ...authData.user, ...(profile || {}) };
            return { data: { user: combinedUser }, error: null };
        },

        signOut: async () => {
            return await supabase.auth.signOut();
        },

        resetPasswordForEmail: async (email: string, redirectTo: string) => {
            return await supabase.auth.resetPasswordForEmail(email, { redirectTo });
        },

        updateUser: async (attributes: any) => {
            return await supabase.auth.updateUser(attributes);
        }
    }
};
